import actions, {
    GET_ADDRESS_VALIDATION,
    GET_WALLET_CONTACTS,
    GET_WALLET_DETAILS,
    POST_SEND_NEX,
    ADD_CONTACT,
    EDIT_CONTACT,
    GET_CONTACT,
    DELETE_CONTACT,
    GET_RECENT_TRANSACTIONS,
    GET_BALANCE_NEX_MONETARY_BASE,
    ADD_WHITELISTERS,
    GET_EMIT_MINT,
    GET_REMOVE_BURN,
    GET_TOTAL_PUBLISHED,
    GET_WHITELISTERS,
    REMOVE_WHITELISTERS,
    GET_W3MM,
    PUT_PAYMENT_REGISTER,
} from './wallet.actions';
import services from './wallet.services';
import { KeyManager } from 'jasper-roche-crypto';
import keysStorage from '../utils/storage/keys';
import signTransaction from '../utils/signTransaction';
import { mapUserTypeToTypeFrom } from '../payment-flow/mapUserTypeToTypeFrom';

const inFlightByAddress = new Map();

const getErrorDetail = (err) => {
    const data = err?.response?.data;
    if (!data) return err?.message || String(err);
    if (data.detail) return String(data.detail);
    if (data.message) return String(data.message);
    if (typeof data === 'object') {
        const parts = Object.entries(data).map(([field, msgs]) => {
            const text = Array.isArray(msgs) ? msgs.join(', ') : String(msgs);
            return `${field}: ${text}`;
        });
        if (parts.length) return parts.join('; ');
    }
    return err?.message || String(err);
};

const isNonceDesyncError = (err) => /nonce desincronizado/i.test(getErrorDetail(err));

const walletMiddleware =
    ({ dispatch, getState }) =>
    (next) =>
    async (action) => {
        const getKeysFromKeystore = (username) => {
            const keyManager = new KeyManager(username);
            const fundsKey = keyManager.deriveFundsKey();
            const publicKey = keyManager.getPublicKey(fundsKey);
            const address = keyManager.getAddress(publicKey);

            return { address, fundsKey };
        };

        next(action);

        const fromGweiToEth = (gwei) => {
            return Math.round(gwei / 1000000000000000) / 1000;
        };

        const amountFormat = (amount) =>
            parseFloat(amount.replaceAll('.', '').replaceAll(',', '.')).toString();

        switch (action.type) {
            case GET_WALLET_DETAILS:
                {
                    const mnemonic = await keysStorage.getMnemonicFromKeystore(
                        getState().profile.user.username,
                    );
                    if (mnemonic) {
                        const { address, fundsKey } = getKeysFromKeystore(
                            getState().profile.user.username,
                        );
                        services
                            .getWalletBalance(address, fundsKey)
                            .then((data) => {
                                const balance = fromGweiToEth(data);
                                dispatch(
                                    actions.getWalletDetailsResponse({
                                        address: address,
                                        balance: balance,
                                    }),
                                );
                            })
                            .catch((err) => dispatch(actions.getWalletDetailsError(err)));
                    }
                }
                break;
            case GET_RECENT_TRANSACTIONS:
                services
                    .getRecentTransactions()
                    .then((res) => dispatch(actions.getRecentTransactionsResponse(res)))
                    .catch((err) => dispatch(actions.getRecentTransactionsError(err)));
                break;
            case GET_ADDRESS_VALIDATION:
                services
                    .getAddressValidation(action.address)
                    .then((res) =>
                        dispatch(actions.getAddressValidationResponse(setInformacionAddress(res))),
                    )
                    .catch((err) => dispatch(actions.getAddressValidationError(err)));
                break;
            case POST_SEND_NEX: {
                const user = getState().profile.user;
                const osId = getState().session.os_id;
                const drogId = getState().session.drug_id;
                const { address, fundsKey } = getKeysFromKeystore(user.username);
                const balance = getState().wallet.data.balance;
                const normalizedAmount = amountFormat(action.address.amount);
                const addressTo = action.address.address || action.address.code;
                const typeFrom = mapUserTypeToTypeFrom(user.type);
                const idFrom = osId || drogId;

                if (!addressTo) {
                    dispatch(
                        actions.postSendNexError({
                            code: 'broadcastError',
                            message: 'Dirección de destino no válida',
                        }),
                    );
                    break;
                }

                if (balance < normalizedAmount) {
                    dispatch(actions.postSendNexError({ message: 'Fondos Insuficientes' }));
                    break;
                }

                if (!typeFrom) {
                    dispatch(
                        actions.postSendNexError({
                            code: 'invalidUserType',
                            message: 'Tipo de usuario no válido para envío de NEX',
                        }),
                    );
                    break;
                }

                if (inFlightByAddress.get(address)) {
                    dispatch(
                        actions.postSendNexError({
                            code: 'inFlight',
                            message: 'Ya hay un envío en curso',
                        }),
                    );
                    break;
                }

                const paymentPayload = {
                    id_from: idFrom,
                    type_from: typeFrom,
                    address_from: address,
                    address_to: addressTo,
                    amount: normalizedAmount,
                };

                const runWalletPayment = async () => {
                    inFlightByAddress.set(address, true);
                    try {
                        dispatch(actions.setSendNexStage('iniciating'));
                        const iniciarRes = await services.postIniciarPagoWallet(paymentPayload);

                        dispatch(actions.setSendNexStage('signing'));
                        const signedTransaction = await signTransaction(
                            iniciarRes.transactionDetails,
                            fundsKey,
                        );

                        dispatch(actions.setSendNexStage('broadcasting'));
                        const registerRes = await services.putPaymentRegister({
                            ...paymentPayload,
                            executionId: iniciarRes.executionId ?? iniciarRes.walletExecutionId,
                            signedTransaction,
                            transactionDetails: iniciarRes.transactionDetails,
                        });

                        const txHash = registerRes.txid || registerRes.transactionHash;
                        dispatch(
                            actions.postSendNexResponse({
                                transactionHash: txHash,
                            }),
                        );
                    } catch (err) {
                        const detail = getErrorDetail(err);
                        if (detail.includes('invalid address')) {
                            dispatch(actions.postSendNexError({ message: 'Address Invalida' }));
                        } else if (isNonceDesyncError(err)) {
                            dispatch(
                                actions.postSendNexError({
                                    code: 'nonceDesync',
                                    message: detail,
                                }),
                            );
                        } else if (/nodo RPC|Invalid URL 'None'/i.test(detail)) {
                            dispatch(
                                actions.postSendNexError({
                                    code: 'broadcastError',
                                    message:
                                        'El backend de testnet no tiene RPC configurado. Contactá al equipo de backend.',
                                }),
                            );
                        } else if (/txid/i.test(detail)) {
                            dispatch(
                                actions.postSendNexError({
                                    code: 'broadcastError',
                                    message: detail,
                                }),
                            );
                        } else if (/network error|econnrefused|failed to fetch/i.test(detail)) {
                            dispatch(
                                actions.postSendNexError({
                                    code: 'broadcastError',
                                    message: detail,
                                }),
                            );
                        } else {
                            dispatch(
                                actions.postSendNexError({
                                    code: 'broadcastError',
                                    message: detail,
                                }),
                            );
                        }
                    } finally {
                        inFlightByAddress.delete(address);
                    }
                };

                runWalletPayment();
                break;
            }
            case ADD_CONTACT:
                services
                    .addContact(action.contact)
                    .then((data) => dispatch(actions.addContactResponse(data)))
                    .catch((err) => dispatch(actions.addContactError(getEmptyFields(err.data))));
                break;
            case EDIT_CONTACT:
                services
                    .editContact(action.contact)
                    .then((data) => dispatch(actions.editContactResponse(data)))
                    .catch((err) => dispatch(actions.editContactError(getEmptyFields(err.data))));
                break;
            case DELETE_CONTACT:
                services
                    .deleteContact(action.contactId)
                    .then((data) => dispatch(actions.deleteContactResponse(data)))
                    .catch((err) => dispatch(actions.deleteContactError(err)));
                break;
            case GET_WALLET_CONTACTS:
                services
                    .getContacts()
                    .then((data) => dispatch(actions.getContactsResponse(data)))
                    .catch((err) => dispatch(actions.getContactsError(err)));
                break;
            case GET_CONTACT:
                services
                    .getContact(action.contactId)
                    .then((data) => dispatch(actions.getContactResponse(data)))
                    .catch((err) => dispatch(actions.getContactError(err)));
                break;
            case GET_W3MM:
                services
                    .getWeb3MM()
                    .then((data) => dispatch(actions.getW3mmResponse(data)))
                    .catch((err) => dispatch(actions.getW3mmError(err)));
                break;
            case GET_BALANCE_NEX_MONETARY_BASE: {
                const { address } = getKeysFromKeystore(getState().profile.user.username);
                services
                    .getWeb3MM()
                    .then((w3mm) => {
                        services
                            .getBalanceNexMonetaryBase(w3mm)
                            .then((balance) =>
                                dispatch(
                                    actions.getBalanceNexMonetaryBaseResponse({
                                        address: w3mm.currentProvider.selectedAddress,
                                        balance: fromGweiToEth(balance),
                                    }),
                                ),
                            )
                            .catch((err) => dispatch(actions.getBalanceNexMonetaryBaseError(err)));
                    })
                    .catch((error) => dispatch(actions.getW3mmError(error)));
                break;
            }

            case GET_TOTAL_PUBLISHED:
                services
                    .getWeb3MM()
                    .then((w3mm) => {
                        services
                            .getTotalPublished(w3mm)
                            .then((balance) =>
                                dispatch(actions.getTotalPublishedResponse(fromGweiToEth(balance))),
                            )
                            .catch((err) => dispatch(actions.getTotalPublishedError(err)));
                    })
                    .catch((error) => dispatch(actions.getW3mmError(error)));
                break;
            case GET_EMIT_MINT:
                services
                    .getWeb3MM()
                    .then((w3mm) => {
                        services
                            .toEmitMint(w3mm, w3mm.currentProvider.selectedAddress, action.mint)
                            .then((response) => {
                                dispatch(actions.getEmitMintResponse(response));
                                dispatch(actions.getTotalPublished());
                            })
                            .catch((error) => {
                                dispatch(actions.getEmitMintError(error));
                            });
                    })
                    .catch((error) => dispatch(actions.getW3mmError(error)));

                break;
            case GET_REMOVE_BURN:
                services
                    .getWeb3MM()
                    .then((w3mm) => {
                        services
                            .toremoveBurn(w3mm, w3mm.currentProvider.selectedAddress, action.burn)
                            .then((response) => {
                                dispatch(actions.getRemoveBurnResponse(response));
                                dispatch(actions.getTotalPublished());
                            })
                            .catch((error) => {
                                dispatch(actions.getRemoveBurnError(error));
                            });
                    })
                    .catch((error) => dispatch(actions.getW3mmError(error)));
                break;

            case GET_WHITELISTERS:
                services
                    .getWeb3MM()
                    .then((w3mm) => {
                        services
                            .getWhitelisters(w3mm, w3mm.currentProvider.selectedAddress)
                            .then((response) => {
                                dispatch(actions.getWhitelistersResponse(response));
                            })
                            .catch((error) => {
                                dispatch(actions.getWhitelistersError(error));
                            });
                    })
                    .catch((error) => dispatch(actions.getW3mmError(error)));
                break;
            case ADD_WHITELISTERS:
                services
                    .getWeb3MM()
                    .then((w3mm) => {
                        services
                            .addToWhitelisters(
                                action.address,
                                w3mm,
                                w3mm.currentProvider.selectedAddress,
                            )
                            .then((response) => {
                                dispatch(actions.addWhitelistersResponse(response));
                                dispatch(actions.getWhitelisters());
                            })
                            .catch((error) => {
                                dispatch(actions.addWhitelistersError(error));
                            });
                    })
                    .catch((error) => dispatch(actions.getW3mmError(error)));
                break;
            case REMOVE_WHITELISTERS:
                services
                    .getWeb3MM()
                    .then((w3mm) => {
                        services
                            .removeFromWhitelisters(
                                action.address,
                                w3mm,
                                w3mm.currentProvider.selectedAddress,
                            )
                            .then((response) => {
                                dispatch(actions.removeWhitelistersResponse(response));
                                dispatch(actions.getWhitelisters());
                            })
                            .catch((error) => {
                                dispatch(actions.removeWhitelistersError(error));
                            });
                    })
                    .catch((error) => dispatch(actions.getW3mmError(error)));
                break;
            case PUT_PAYMENT_REGISTER: {
                services
                    .putPaymentRegister(action.payload)
                    .then((res) => dispatch(actions.putPaymentRegisterResponse(res)))
                    .catch((err) => dispatch(actions.putPaymentRegisterError(err)));
                break;
            }
        }

        const getEmptyFields = (err) => {
            const emptyFields = [];
            if (err.nombre) {
                emptyFields.push('nombre');
            }
            if (err.address) {
                emptyFields.push('address');
            }
            if (err.email) {
                emptyFields.push('email');
            }
            return emptyFields;
        };

        const setInformacionAddress = (infoAddress) => {
            return {
                ...infoAddress,
                nombre: infoAddress?.nombre || infoAddress?.descr,
            };
        };
    };

export default walletMiddleware;
